import axios from "axios";
import { openAuthenticatedDocument } from "@/lib/openAuthenticatedDocument";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const optionalEmail = (value) => value?.trim() || null;

const validationFieldLabels = {
  name: "Name",
  email: "E-Mail-Adresse",
  responsible_party: "Verantwortliche Person",
};

export const adminContractErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail.map((entry) => {
      const location = Array.isArray(entry?.loc)
        ? entry.loc.filter((part) => part !== "body")
        : [];
      const field = location[location.length - 1];
      const label = validationFieldLabels[field] || location.join(".") || "Eingabe";
      const message = field === "email"
        ? "Bitte gib eine vollständige gültige E-Mail-Adresse ein."
        : typeof entry?.msg === "string" ? entry.msg : "ist ungültig";
      return `${label}: ${message}`;
    }).filter(Boolean);
    if (messages.length) return messages.join(" · ");
  }
  return error?.message || fallback;
};

export const euroOverrideToCents = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const normalized = String(value).trim().replace(",", ".");
  const amount = Number(normalized);
  if (!Number.isFinite(amount) || amount <= 0) throw new Error("Ungültiger individueller Preis");
  return Math.round(amount * 100);
};

export const buildContractDraftPayload = ({
  wacheId,
  abteilungId,
  name,
  email,
  tariffCode,
  durationMonths,
  startDate,
  overrideEuro,
}) => ({
  wache_id: wacheId,
  abteilung_id: abteilungId,
  responsible_party: { name: name.trim(), email: optionalEmail(email) },
  terms: {
    tariff_code: tariffCode,
    duration_months: Number(durationMonths),
    start_date: startDate,
    price_override_cents: euroOverrideToCents(overrideEuro),
  },
});

export const buildLegacyMigrationPayload = ({ name, email }) => ({
  responsible_party: { name: name.trim(), email: optionalEmail(email) },
});

const creditEligibleStatuses = new Set(["awaiting_payment", "active", "change_pending"]);

export const canGrantCredit = (contract) => Boolean(
  contract &&
  !contract.archived_at &&
  !contract.cancel_requested_at &&
  creditEligibleStatuses.has(contract.status)
);

export const buildCreditPayload = ({ months, reason, note }) => {
  const normalizedMonths = Number(months);
  if (!Number.isInteger(normalizedMonths) || normalizedMonths <= 0) {
    throw new Error("Bitte eine positive Anzahl voller Monate eingeben");
  }
  if (!["referral", "marketing", "other"].includes(reason)) {
    throw new Error("Bitte einen Grund auswählen");
  }
  return {
    months: normalizedMonths,
    reason,
    note: note?.trim() || null,
  };
};

export const creditReasonLabel = (reason) => ({
  referral: "Werbeprogramm",
  marketing: "Marketing",
  other: "Sonstiges",
}[reason] || "Gratis-Monate");

export const creditStatusLabel = (status) => ({
  pending: "Vorgemerkt",
  applied: "Eingebucht",
  voided: "Storniert",
}[status] || status);

export const buildDepartmentPatchPayload = ({
  name,
  username,
  password,
  status,
  billingMode,
  amountEuro,
  interval,
  firstPayment,
}) => {
  const payload = {
    name: name.trim(),
    user_username: username.trim(),
    status,
  };
  if (password) payload.user_password = password;
  if (billingMode && billingMode !== "contract") {
    const amount = Number(String(amountEuro).trim().replace(",", "."));
    if (!Number.isFinite(amount) || amount <= 0) throw new Error("Ungültiger Legacy-Betrag");
    payload.betrag = amount;
    payload.intervall = interval;
    payload.erstmalige_zahlung = firstPayment;
  }
  return payload;
};

const pendingStatuses = new Set(["draft", "invited", "awaiting_payment"]);

export const resolveDepartmentContracts = (department, contracts) => {
  const relevant = contracts
    .filter((contract) => !contract.abteilung_id || contract.abteilung_id === department.id)
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  let current = relevant.find((contract) => contract.id === department.current_contract_id) || null;
  if (current?.archived_at) current = null;
  let pending = relevant.find(
    (contract) =>
      !contract.archived_at &&
      contract.id !== current?.id &&
      (contract.access_status === "pending" || pendingStatuses.has(contract.status))
  ) || null;
  if (!current && pending) {
    current = pending;
    pending = null;
  }
  const history = relevant;
  return { current, pending, history };
};

export const adminContractApi = {
  listForWache: (wacheId) => axios.get(`${API}/admin/contracts`, { params: { wache_id: wacheId } }),
  legacyPreflight: (wacheId, departmentId) =>
    axios.get(`${API}/admin/departments/${departmentId}/legacy-contract/preflight`, {
      params: { wache_id: wacheId },
    }),
  createLegacyDraft: (wacheId, departmentId, payload) =>
    axios.post(`${API}/admin/departments/${departmentId}/legacy-contract`, payload, {
      params: { wache_id: wacheId },
    }),
  openCopy: (contractId) => openAuthenticatedDocument(async () => {
    const response = await axios.get(`${API}/admin/contracts/${contractId}/copy`, {
      responseType: "blob",
    });
    return response.data;
  }),
  createDraft: (payload) => axios.post(`${API}/admin/contracts`, payload),
  discard: (contractId) => axios.delete(`${API}/admin/contracts/${contractId}`),
  issueInvitation: (contractId, sendEmail = false) =>
    axios.post(`${API}/admin/contracts/${contractId}/invitation`, { send_email: sendEmail }),
  recordPayment: (contractId, payload) =>
    axios.post(`${API}/admin/contracts/${contractId}/payments`, payload),
  previewCredit: (contractId, payload) =>
    axios.post(`${API}/admin/contracts/${contractId}/credits/preview`, { months: payload.months }),
  grantCredit: (contractId, payload, idempotencyKey) =>
    axios.post(`${API}/admin/contracts/${contractId}/credits`, payload, {
      headers: { "Idempotency-Key": idempotencyKey },
    }),
  listCredits: (contractId) => axios.get(`${API}/admin/contracts/${contractId}/credits`),
  previewVoidCredit: (contractId, creditId) =>
    axios.post(`${API}/admin/contracts/${contractId}/credits/${creditId}/void-preview`),
  voidCredit: (contractId, creditId, payload) =>
    axios.post(`${API}/admin/contracts/${contractId}/credits/${creditId}/void`, payload),
  correctLegacyPaymentDue: (contractId, payload) =>
    axios.patch(`${API}/admin/contracts/${contractId}/legacy-payment-due`, payload),
  cancel: (contractId, payload) => axios.post(`${API}/admin/contracts/${contractId}/cancel`, payload),
  revokeCancellation: (contractId, payload) =>
    axios.post(`${API}/admin/contracts/${contractId}/cancel/revoke`, payload),
  archive: (contractId, note = null) =>
    axios.post(`${API}/admin/contracts/${contractId}/archive`, { note }),
  restore: (contractId) => axios.post(`${API}/admin/contracts/${contractId}/restore`),
  reactivate: (contractId) => axios.post(`${API}/admin/contracts/${contractId}/reactivate`),
  block: (contractId, reason) =>
    axios.post(`${API}/admin/contracts/${contractId}/block`, { reason }),
  successor: (contractId, responsibleParty) =>
    axios.post(`${API}/admin/contracts/${contractId}/successor`, { responsible_party: responsibleParty }),
  proposeTermsChange: (contractId, terms) =>
    axios.post(`${API}/admin/contracts/${contractId}/terms-change`, terms),
};
